import { createMuiTheme } from "@material-ui/core/styles";
import { teal } from "@material-ui/core/colors";

const colorPrimary = {
  light: "#BDC5CD",
  main: "#666666",
  disabled: "#555555",
  dark: "#333333",
  contrastText: "#FFF",
  error: "#AE1409",
  green: {
    light: teal[100],
    dark: "#004747",
  },
};

const colorSecondary = {
  light: "#FFF",
  main: "#127DA9",
  dark: "#0D5876",
  contrastText: "#FFF",
};

export const theme = createMuiTheme({
  typography: {
    fontFamily: ["Nunito", "sans-serif"].join(","),
    htmlFontSize: 16,
  },

  palette: {
    primary: colorPrimary,
    secondary: colorSecondary,
  },

  props: {
    MuiTypography: {
      variantMapping: {
        body2: "span",
        subtitle1: "p",
      },
    },
  },

  overrides: {
    MuiCssBaseline: {
      "@global": {
        a: {
          color: colorSecondary.main,
          textDecoration: "none",
          fontWeight: 700,
        },
        html: {
          height: "100%",
        },
        body: {
          height: "100%",
          backgroundColor: "#FFF",
        },
        "#root": {
          height: "100%",
        },
      },
    },

    MuiAppBar: {
      colorPrimary: {
        backgroundColor: colorSecondary.light,
        color: colorPrimary.main,
      },
    },

    MuiButton: {
      root: {
        textTransform: "none",
      },
      text: {
        fontSize: ".75rem",
        color: colorSecondary.main,
        "&:hover": {
          color: colorSecondary.dark,
        },
      },
      contained: {
        backgroundColor: colorSecondary.main,
        color: colorSecondary.contrastText,
        padding: "10px 40px",
        textTransform: "none",
        fontWeight: 500,
        fontSize: "1rem",
        borderRadius: 5,
        minWidth: 125,
        "&:hover": {
          backgroundColor: colorSecondary.dark,
        },
      },
      outlined: {
        borderColor: colorPrimary.main,
        color: colorPrimary.main,
        padding: "10px 40px",
        textTransform: "none",
        fontWeight: 500,
        fontSize: "1rem",
        borderRadius: 5,
        minWidth: 125,
        "&:hover": {
          backgroundColor: colorPrimary.light,
        },
      },
    },

    MuiFormLabel: {
      root: {
        "&$error": {
          color: colorPrimary.error,
        },
        "&$focused": {
          color: colorSecondary.main,
        },
      },

      colorSecondary: {
        color: colorPrimary.dark,
        fontWeight: "bold",

        "&$focused": {
          color: colorPrimary.dark,
        },
      },
    },

    MuiFormControlLabel: {
      root: {
        "&$disabled": {
          cursor: "not-allowed",
        },
      },
    },

    MuiFormHelperText: {
      root: {
        "&$error": {
          color: colorPrimary.error,
        },
      },
    },

    MuiInput: {
      root: {
        color: colorPrimary.main,
        fontSize: ".75rem",
      },
      underline: {
        "&:before": {
          borderBottom: `1px solid ${colorPrimary.main}`,
          opacity: ".3",
        },
        "&:after": {
          borderBottom: `2px solid ${colorSecondary.main}`,
        },
        "&$error::after": {
          borderBottomColor: colorPrimary.error,
        },
      },
    },

    MuiInputBase: {
      root: {
        "&$disabled": {
          color: colorPrimary.disabled,
          cursor: "not-allowed !important",
        },
      },
      input: {
        "@media only screen and (max-width: 600px)": {
          fontSize: "16pt",
        },
      },
    },

    MuiInputLabel: {
      root: {
        fontSize: ".75rem",
      },
    },

    MuiLinearProgress: {
      root: {
        borderRadius: 20,
      },

      barColorPrimary: {
        backgroundColor: "#49B581",
      },
    },

    MuiPickersDay: {
      daySelected: {
        backgroundColor: colorPrimary.main,
        "&:hover": {
          backgroundColor: colorPrimary.main,
        },
      },
    },

    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: colorPrimary.main,
      },
    },

    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: colorPrimary.light,
        },
      },
    },

    MuiStepper: {
      root: {
        padding: 0,
        backgroundColor: "#F6F7FB",
      },
    },

    MuiTab: {
      root: {
        marginRight: 30,
        padding: 0,
        minWidth: 0,
        "@media (min-width: 600px)": {
          padding: 0,
          minWidth: 0,
        },
      },
      wrapper: {
        color: colorPrimary.dark,
        fontWeight: "bold",
      },
    },

    MuiTabs: {
      indicator: {
        height: 4,
      },
    },

    MuiTextField: {
      root: {
        width: "100%",
      },
    },

    MuiTypography: {
      colorTextSecondary: {
        color: colorSecondary.contrastText,
      },

      h1: {
        fontSize: "3rem",
        fontFamily: "proxima-soft, serif",
        fontWeight: 400,
        color: colorPrimary.dark,
      },

      h2: {
        fontSize: "1.2rem",
        lineHeight: "1.5rem",
        fontFamily: "Nunito, sans-serif",
        fontWeight: "bold",
        color: colorPrimary.dark,
      },

      h3: {
        fontSize: "1rem",
        lineHeight: "1.25rem",
        fontFamily: "proxima-soft, sans-serif",
        fontWeight: 300,
        color: colorPrimary.main,
        letterSpacing: 0.84,
      },

      h5: {
        fontWeight: 700,
      },

      h6: {
        fontWeight: 700,
      },

      body1: {
        fontSize: "1rem",
      },

      // span
      body2: {
        fontSize: "1rem",
        color: colorPrimary.main,
        display: "block",
      },

      // p
      subtitle1: {
        fontSize: ".85rem",
        color: colorPrimary.main,
        lineHeight: 1,
      },
    },
  },
});
